import { graphql, useStaticQuery } from 'gatsby';
import React, { useState } from 'react';
import Grid from 'src/app/atoms/Layout/Grid/Grid';
import { TypographyProps } from 'src/app/atoms/Typography/Typography';
import { TestimonialCardProps } from 'src/app/molecules/TestimonialCard/TestimonialCard';
import TestimonialVideoCardCarouselWithTextBlock from 'src/app/molecules/TestimonialVideoCardCarouselWithTextBlock/TestimonialVideoCardCarouselWithTextBlock';
import OverlayVideoPortal from 'src/app_previous/elements/video/OverlayVideoPortal';
import { IContentfulImage, IContentfulVideo, IStaticMixedQuery } from 'src/types/contentful';

const TestimonialsFeedCarousel = (): JSX.Element => {
  const assets = useStaticQuery<IStaticMixedQuery<IContentfulImage, IContentfulVideo>>(graphql`
    {
      testimonialCard1: contentfulAsset(contentful_id: { eq: "3ctoDNFJdjCx83e8m1JWnM" }) {
        gatsbyImageData(layout: CONSTRAINED, placeholder: BLURRED, width: 1000)
      }
      testimonialCard2: contentfulAsset(contentful_id: { eq: "25V69VLlPK5dIgg9gRDXnd" }) {
        gatsbyImageData(layout: CONSTRAINED, placeholder: BLURRED, width: 1000)
      }
      testimonialCard3: contentfulAsset(contentful_id: { eq: "2IQf03rK3X3DKTy59WOSU2" }) {
        gatsbyImageData(layout: CONSTRAINED, placeholder: BLURRED, width: 1000)
      }
      testimonialCard4: contentfulAsset(contentful_id: { eq: "4vIOSuXaEidsHAoUBVRLMT" }) {
        gatsbyImageData(layout: CONSTRAINED, placeholder: BLURRED, width: 1000)
      }
      testimonialCard5: contentfulAsset(contentful_id: { eq: "O1ZqBLijHpzoqcwNIfQeF" }) {
        gatsbyImageData(layout: CONSTRAINED, placeholder: BLURRED, width: 1000)
      }
      testimonialCard6: contentfulAsset(contentful_id: { eq: "5aLLsGEjNbwsagV1TvLf96" }) {
        gatsbyImageData(layout: CONSTRAINED, placeholder: BLURRED, width: 1000)
      }
      testimonialCard7: contentfulAsset(contentful_id: { eq: "3hvMcFu1SiCOirO9665LLy" }) {
        gatsbyImageData(layout: CONSTRAINED, placeholder: BLURRED, width: 1000)
      }
      testimonialCard8: contentfulAsset(contentful_id: { eq: "40c8OhIFKWxEgg7RrBIVPL" }) {
        gatsbyImageData(layout: CONSTRAINED, placeholder: BLURRED, width: 1000)
      }
      testimonialCard9: contentfulAsset(contentful_id: { eq: "33dWSKR7PjyqIUy5Wksx2I" }) {
        gatsbyImageData(layout: CONSTRAINED, placeholder: BLURRED, width: 1000)
      }
      testimonialPreviewVideo1: contentfulAsset(contentful_id: { eq: "4zsep4LyYI7vZOFFjM1Mwo" }) {
        file {
          url
        }
      }
      testimonialPreviewVideo2: contentfulAsset(contentful_id: { eq: "6TTm4xeh6wSuYFjrOBOW2U" }) {
        file {
          url
        }
      }
      testimonialPreviewVideo3: contentfulAsset(contentful_id: { eq: "6WpElVbEESpOUp3fmULB9x" }) {
        file {
          url
        }
      }
      testimonialPreviewVideo4: contentfulAsset(contentful_id: { eq: "5KL6kembY429eZNOKdAWTR" }) {
        file {
          url
        }
      }
      testimonialPreviewVideo5: contentfulAsset(contentful_id: { eq: "21YrrkselqTJPYHOEqxkid" }) {
        file {
          url
        }
      }
      testimonialPreviewVideo6: contentfulAsset(contentful_id: { eq: "5V3hsOpkWYSofZbss9FnQG" }) {
        file {
          url
        }
      }
      testimonialPreviewVideo7: contentfulAsset(contentful_id: { eq: "4YiDWft9pKcboHYWMOsm5X" }) {
        file {
          url
        }
      }
      testimonialPreviewVideo8: contentfulAsset(contentful_id: { eq: "3xrLdoqWeDXDpZtTw5z8ng" }) {
        file {
          url
        }
      }
      testimonialPreviewVideo9: contentfulAsset(contentful_id: { eq: "6iZ994vlSGXDDTvxrIGBa7" }) {
        file {
          url
        }
      }
    }
  `);

  const wistiaVideosIds = {
    'Marie Forleo': 'ao8qqan8r7',
    'Jefferson Fisher': 'b3o5f8u6xh',
    'Seth Godin': 'ttdq5mvl6z',
    'Tiffany Aliche': 'p7tkbk28x8',
    'Dan Martell': '9flvkmwnk7',
    'Ashley Fox': 'm3uk2qr4xk',
    'Nathan Barry': 's6r6kt85iq',
    'Luvvie Ajayi Jones': '98xwcjw88p',
    'Melissa Urban': 'hq0c6cuisr',
  };

  const defaultTitleProps: TypographyProps = {
    type: 'paragraph',
    size: 'xl',
    fontWeight: 'font-weight-semibold',
  };

  const defaultSubheadingProps: TypographyProps = {
    type: 'paragraph',
    size: 'md',
  };

  const imageMaxWidth = 360;

  const defaultProps = {
    imageMaxWidth,
    playIconOnHover: true,
  };

  const onClick = (videoId: string) => () => {
    setVideoModalId(videoId);
  };

  const cards: TestimonialCardProps[] = [
    {
      testimonialTitle: {
        text: 'Nothing has been as easy as Mighty—we love it',
        ...defaultTitleProps,
      },
      testimonialText: {
        text: 'Marie Forleo',
        ...defaultSubheadingProps,
      },
      image: {
        image: assets.testimonialCard1.gatsbyImageData,
        alt: 'Testimonial Card 1',
      },
      posterVideo: {
        src: assets.testimonialPreviewVideo1.file.url,
      },
      ...defaultProps,
      onClick: onClick(wistiaVideosIds['Marie Forleo']),
    },
    {
      testimonialTitle: {
        text: 'Highly, highly, highly recommend',
        ...defaultTitleProps,
      },
      testimonialText: {
        text: 'Jefferson Fisher',
        ...defaultSubheadingProps,
      },
      image: {
        image: assets.testimonialCard2.gatsbyImageData,
        alt: 'Testimonial Card 2',
      },
      posterVideo: {
        src: assets.testimonialPreviewVideo2.file.url,
      },
      ...defaultProps,
      onClick: onClick(wistiaVideosIds['Jefferson Fisher']),
    },
    {
      testimonialTitle: {
        text: 'It was an instant YES',
        ...defaultTitleProps,
      },
      testimonialText: {
        text: 'Seth Godin',
        ...defaultSubheadingProps,
      },
      image: {
        image: assets.testimonialCard3.gatsbyImageData,
        alt: 'Testimonial Card 3',
      },
      posterVideo: {
        src: assets.testimonialPreviewVideo3.file.url,
      },
      ...defaultProps,
      onClick: onClick(wistiaVideosIds['Seth Godin']),
    },
    {
      testimonialTitle: {
        text: 'We’re at $50M',
        ...defaultTitleProps,
      },
      testimonialText: {
        text: 'Tiffany Aliche',
        ...defaultSubheadingProps,
      },
      image: {
        image: assets.testimonialCard4.gatsbyImageData,
        alt: 'Testimonial Card 4',
      },
      posterVideo: {
        src: assets.testimonialPreviewVideo4.file.url,
      },
      ...defaultProps,
      onClick: onClick(wistiaVideosIds['Tiffany Aliche']),
    },
    {
      testimonialTitle: {
        text: 'I’m pumped for this community',
        ...defaultTitleProps,
      },
      testimonialText: {
        text: 'Dan Martell',
        ...defaultSubheadingProps,
      },
      image: {
        image: assets.testimonialCard5.gatsbyImageData,
        alt: 'Testimonial Card 5',
      },
      posterVideo: {
        src: assets.testimonialPreviewVideo5.file.url,
      },
      ...defaultProps,
      onClick: onClick(wistiaVideosIds['Dan Martell']),
    },
    {
      testimonialTitle: {
        text: 'We crossed 7-figures in recurring revenue',
        ...defaultTitleProps,
      },
      testimonialText: {
        text: 'Ashley Fox',
        ...defaultSubheadingProps,
      },
      image: {
        image: assets.testimonialCard6.gatsbyImageData,
        alt: 'Testimonial Card 6',
      },
      posterVideo: {
        src: assets.testimonialPreviewVideo6.file.url,
      },
      ...defaultProps,
      onClick: onClick(wistiaVideosIds['Ashley Fox']),
    },
    {
      testimonialTitle: {
        text: 'Gina is SO good at teaching all of this',
        ...defaultTitleProps,
      },
      testimonialText: {
        text: 'Nathan Barry',
        ...defaultSubheadingProps,
      },
      image: {
        image: assets.testimonialCard7.gatsbyImageData,
        alt: 'Testimonial Card 7',
      },
      posterVideo: {
        src: assets.testimonialPreviewVideo7.file.url,
      },
      ...defaultProps,
      onClick: onClick(wistiaVideosIds['Nathan Barry']),
    },
    {
      testimonialTitle: {
        text: 'I keep raising the price because the value goes UP',
        ...defaultTitleProps,
      },
      testimonialText: {
        text: 'Luvvie Ajayi Jones',
        ...defaultSubheadingProps,
      },
      image: {
        image: assets.testimonialCard8.gatsbyImageData,
        alt: 'Testimonial Card 8',
      },
      posterVideo: {
        src: assets.testimonialPreviewVideo8.file.url,
      },
      ...defaultProps,
      onClick: onClick(wistiaVideosIds['Luvvie Ajayi Jones']),
    },
    {
      testimonialTitle: {
        text: 'I’m really excited to keep building on Mighty',
        ...defaultTitleProps,
      },
      testimonialText: {
        text: 'Melissa Urban',
        ...defaultSubheadingProps,
      },
      image: {
        image: assets.testimonialCard9.gatsbyImageData,
        alt: 'Testimonial Card 9',
      },
      posterVideo: {
        src: assets.testimonialPreviewVideo9.file.url,
      },
      ...defaultProps,
      onClick: onClick(wistiaVideosIds['Melissa Urban']),
    },
  ];

  const [videoModalId, setVideoModalId] = useState<string | null>(null);

  return (
    <>
      <Grid>
        <TestimonialVideoCardCarouselWithTextBlock
          heading={{
            type: 'heading',
            text: 'We don’t just have customers. We have believers.',
            size: 'md',
          }}
          supportingText={{
            type: 'paragraph',
            text: 'When it comes to the world’s most original and interesting thinkers, Mighty is the first and final word on community.',
            size: 'xl',
            color: 'text-tertiary',
          }}
          button={{
            children: 'Explore Case Studies',
            variant: 'secondary',
            size: 'xl',
            href: '/case-studies',
          }}
          cards={cards}
        />
      </Grid>
      {/* TODO (MKT-9345): Migrate OverlayVideo and its related elements to the new app folder and architecture */}
      <OverlayVideoPortal
        overlayVisible={!!videoModalId}
        setOverlayVisible={(opened) => {
          if (!opened) {
            setVideoModalId(null);
          }
        }}
        isVertical
        wistiaId={videoModalId}
        videoLabel='Testimonial Video'
      />
    </>
  );
};

export default TestimonialsFeedCarousel;
